import dayjs from "dayjs";
import { ChevronLeft, Download, Upload, X, ShieldCheck, User, Package, Calendar, FileText, MessageSquare } from "lucide-react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "../../Modal/Modal";
import { Loading } from "../../Loading";
import { useDpc, useDpcMessages, useUserPartnerInformation } from "../../../hooks";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../../firebase";
import { updateDpcMessageStatus } from "../../../services/dpc/dpc.api";

const STATUS_OPTIONS = [
  { value: 'unread', label: 'Unread', color: 'bg-red-100 text-red-800' },
  { value: 'read', label: 'Read', color: 'bg-blue-100 text-blue-800' },
  { value: 'wip', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-800' },
];

const DpcModal = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { userPartnerInformation } = useUserPartnerInformation(user?.uid);
  const partnerId = userPartnerInformation?.data?.partnerId;

  // Fetch DPC Data
  const { dpc, isLoading: isDpcLoading } = useDpc(id);
  const dpcData = dpc?.data;

  // Fetch Messages for this DPC
  const { messages, isLoading: isMessagesLoading, refetch: refetchMessages } = useDpcMessages(partnerId, id);

  // Tabs: 0 = Info, 1 = Messages
  const [activeTab, setActiveTab] = useState(0);

  const handleStatusChange = async (messageId: string, newStatus: string) => {
    if (!partnerId) return;
    try {
      await updateDpcMessageStatus(partnerId, messageId, newStatus);
      refetchMessages();
    } catch (error) {
      console.error("Failed to update status", error);
      alert("Failed to update status");
    }
  };

  if (isDpcLoading) {
    return (
      <Modal visible showClose backdropClose={false} onClose={() => navigate(-1)}>
        <div className="w-screen md:w-[800px] h-[400px] flex items-center justify-center">
          <Loading />
        </div>
      </Modal>
    );
  }

  if (!dpcData) {
    return (
      <Modal visible showClose backdropClose={false} onClose={() => navigate(-1)}>
        <div className="w-screen md:w-[800px] p-8 text-center text-red-500">
          DPC not found.
        </div>
      </Modal>
    );
  }

  return (
    <Modal visible showClose backdropClose={false} onClose={() => navigate(-1)}>
      <div className="w-screen md:w-[1050px] sm:w-[500px] bg-white rounded-lg overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h1 className="text-2xl font-bold">{dpcData.product?.name || dpcData.model || "Product Details"}</h1>
            <p className="text-gray-500 text-sm mt-1">DPC ID: {id}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6 gap-6">
          <button
            onClick={() => setActiveTab(0)}
            className={`py-4 text-sm font-medium border-b-2 transition ${activeTab === 0 ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab(1)}
            className={`py-4 text-sm font-medium border-b-2 transition ${activeTab === 1 ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'}`}
          >
            Messages
          </button>
        </div>

        {/* Content */}
        <div className="p-6 h-[70vh] overflow-y-auto">

          {/* Overview Tab */}
          {activeTab === 0 && (
            <div className="flex flex-col gap-8">
              {/* Product Details Grid */}
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
                    <Package size={20} /> Product Info
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">Brand</span>
                      <span className="font-medium">{dpcData.brand?.name}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">Model</span>
                      <span className="font-medium">{dpcData.model}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">SKU</span>
                      <span className="font-medium">{dpcData.sku || "N/A"}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">Serial Number</span>
                      <span className="font-medium text-gray-800 bg-gray-100 px-2 py-0.5 rounded text-xs tracking-wider">
                        {dpcData.serialNumber || dpcData.serial || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">Condition</span>
                      <span className="font-medium">{dpcData.condition || "N/A"}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
                    <Calendar size={20} /> Dates & Warranty
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">Purchased Date</span>
                      <span className="font-medium">
                        {dpcData.dateOfPurchase ? dayjs(dpcData.dateOfPurchase).format("DD MMM YYYY") : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-500">Warranty Ends</span>
                      <div className="text-right">
                        <div className="font-medium">
                          {dpcData.warranty ? dayjs(dpcData.warranty).format("DD MMM YYYY") : "N/A"}
                        </div>
                        {dpcData.warranty && (
                          <span className={`text-xs ${dayjs(dpcData.warranty).isAfter(dayjs()) ? 'text-green-600' : 'text-red-500'}`}>
                            {dayjs(dpcData.warranty).isAfter(dayjs()) ? 'Active' : 'Expired'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
                  <FileText size={20} /> Documents
                </h3>
                {dpcData.paperworkFiles && dpcData.paperworkFiles.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {dpcData.paperworkFiles.map((doc: any, i: number) => (
                      <a
                        key={i}
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition"
                      >
                        <div className="w-10 h-10 bg-indigo-50 rounded flex items-center justify-center text-indigo-600 font-bold text-xs uppercase">
                          {doc.fileType || 'Doc'}
                        </div>
                        <div className="overflow-hidden">
                          <p className="text-sm font-medium truncate">{doc.name || doc.paperworkType || `Document ${i + 1}`}</p>
                          <p className="text-xs text-gray-500">Click to view</p>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No documents available.</p>
                )}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 1 && (
            <div>
              {isMessagesLoading ? (
                <div className="flex justify-center p-8"><Loading /></div>
              ) : messages?.data && messages.data.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {messages.data.map((msg: any) => (
                    <div key={msg.id} className="border p-4 rounded-lg bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <span className="bg-black text-white px-2 py-0.5 rounded text-xs font-bold uppercase">
                            {msg.subject?.match(/^\[(.*?)\]/)?.[1] || "Message"}
                          </span>
                          <span className="font-semibold text-gray-900">{msg.subject?.replace(/^\[.*?\]\s*/, "")}</span>
                        </div>

                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {dayjs(msg.createdAt).format("DD MMM YYYY, HH:mm")}
                          </span>
                          <select
                            className={`text-xs font-medium px-2 py-1 rounded border-none outline-none cursor-pointer ${STATUS_OPTIONS.find(o => o.value === (msg.status || 'unread'))?.color}`}
                            value={msg.status || 'unread'}
                            onChange={(e) => handleStatusChange(msg.id, e.target.value)}
                          >
                            {STATUS_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value} className="bg-white text-black">
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 border-b pb-2 mb-2">From: {msg.replyTo}</p>
                      <p className="text-sm bg-white p-3 rounded border text-gray-700 whitespace-pre-wrap">
                        {msg.text?.replace(/From:.*\n\n/, "").replace(/---\nProduct:.*/, "")}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <MessageSquare size={48} className="mb-4 opacity-50" />
                  <p>No messages found for this product.</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </Modal>
  );
};

export default DpcModal;
